'use client'

import { AsYouTypeFormatter, PhoneNumberUtil } from 'google-libphonenumber'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Form, Formik } from 'formik'
import * as Yup from 'yup'
import { OTPInput, REGEXP_ONLY_DIGITS } from 'input-otp'
import { signIn } from 'next-auth/react'
import FieldError from '@/components/FieldError'
import Label from '@/components/Label'
import { useRouter, useSearchParams } from 'next/navigation'
import { useQueryState } from 'nuqs'
import { requestMagicLinkCode, signInPhoneSearchParams } from '@/lib/magic-link-client'
import { getSafeCallbackUrl } from '@/util/get-safe-callback-url'

type MagicLinkMethod = 'phone' | 'email'

type MagicLinkFormValues = {
  method: MagicLinkMethod
  email: string
  phone: string
}

const phoneUtil = PhoneNumberUtil.getInstance()

function formatPartialPhoneNumber(phoneNumber: string, formatter = new AsYouTypeFormatter('US')) {
  if (!phoneNumber) return ''

  formatter.clear()
  let formattedNumber = ''
  phoneNumber
    .replace(/[^0-9]/g, '')
    .split('')
    .forEach(digit => {
      formattedNumber = formatter.inputDigit(digit)
    })
  return formattedNumber
}

function OtpSlotsInput(props: {
  value: string
  onChange: (value: string) => void
  disabled?: boolean
  autoFocus?: boolean
  'aria-label'?: string
}) {
  const { value, onChange, disabled, autoFocus } = props

  return (
    <OTPInput
      maxLength={6}
      value={value}
      onChange={value => onChange(value.replace(/\D/g, '').slice(0, 6))}
      pattern={REGEXP_ONLY_DIGITS}
      autoComplete="one-time-code"
      autoFocus={autoFocus}
      aria-label={props['aria-label'] ?? '6-digit sign-in code'}
      disabled={disabled}
      containerClassName="mt-1 flex items-center justify-between gap-2"
      render={({ slots }) => (
        <>
          {slots.map((slot, index) => (
            <div
              key={index}
              className={`relative flex h-12 w-10 items-center justify-center rounded-md border text-lg font-semibold tabular-nums transition ${
                slot.isActive
                  ? 'border-indigo-500 ring-indigo-500 ring-1'
                  : disabled
                    ? 'border-gray-200 bg-gray-50 text-gray-400'
                    : 'border-gray-300 text-gray-800'
              }`}
            >
              <span>{slot.char ?? ''}</span>
              {slot.hasFakeCaret && (
                <span className="pointer-events-none absolute inset-y-3 w-px animate-pulse bg-gray-700" />
              )}
            </div>
          ))}
        </>
      )}
    />
  )
}

const FormSchema = Yup.object({
  method: Yup.mixed<MagicLinkMethod>().oneOf(['email', 'phone']).required(),
  email: Yup.string()
    .trim()
    .when('method', {
      is: 'email',
      // biome-ignore lint/suspicious/noThenProperty
      then: schema => schema.email('Enter a valid email').required('Email is required'),
      otherwise: schema => schema.notRequired(),
    }),
  phone: Yup.string()
    .trim()
    .when('method', {
      is: 'phone',
      // biome-ignore lint/suspicious/noThenProperty
      then: schema =>
        schema.required('Phone number is required').test('is-valid-phone', 'Enter a valid phone number', value => {
          if (!value?.trim()) return false
          try {
            const parsed = phoneUtil.parseAndKeepRawInput(value, 'US')
            return phoneUtil.isValidNumber(parsed)
          } catch {
            return false
          }
        }),
      otherwise: schema => schema.notRequired(),
    }),
})

export default function MagicLinkSignInForm() {
  const router = useRouter()
  const searchParams = useSearchParams()
  const [phoneFromUrl, setPhoneFromUrl] = useQueryState('phone', signInPhoneSearchParams.phone)
  const [codeSent, setCodeSent] = useQueryState('codeSent', signInPhoneSearchParams.codeSent)
  const [callbackUrlFromQuery] = useQueryState('callbackUrl', signInPhoneSearchParams.callbackUrl)
  const initializedCodeStepFromParams = useRef(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  const [accountNotice, setAccountNotice] = useState<string | null>(null)
  const [phoneDeliveryMessage, setPhoneDeliveryMessage] = useState<string | null>(null)
  const [emailDeliveryMessage, setEmailDeliveryMessage] = useState<string | null>(null)
  const [phoneCodeStep, setPhoneCodeStep] = useState(false)
  const [pendingPhoneIdentifier, setPendingPhoneIdentifier] = useState('')
  const [otpCode, setOtpCode] = useState('')
  const [isVerifyingCode, setIsVerifyingCode] = useState(false)
  const [isResendingCode, setIsResendingCode] = useState(false)
  const [resendCooldownSeconds, setResendCooldownSeconds] = useState(0)
  /** Prevents duplicate submissions from double-tap / multiple touch events (common on mobile). */
  const isSubmittingRef = useRef(false)
  /** Safari mobile can queue a second submit event after the first completes (touch + synthetic click or delayed fire).
   * Ref guard alone stops re-entry; this window stops that delayed second event from sending another request. */
  const lastSubmitTimeRef = useRef(0)
  const initialValues = useMemo<MagicLinkFormValues>(() => {
    const prefilledPhone = phoneFromUrl ? formatPartialPhoneNumber(phoneFromUrl) : ''
    return {
      method: 'phone',
      email: '',
      phone: prefilledPhone,
    }
  }, [phoneFromUrl])

  const callbackUrl = getSafeCallbackUrl(
    callbackUrlFromQuery ??
      searchParams?.get('callbackUrl') ??
      searchParams?.get('callbackURL') ??
      undefined,
  )

  const verifyPhoneCode = async () => {
    if (otpCode.length !== 6 || !pendingPhoneIdentifier) {
      setError('Please enter a valid 6-digit code.')
      return
    }

    setError(null)
    setIsVerifyingCode(true)
    try {
      const res = await fetch('/api/auth/magic-link/verify-code', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ identifier: pendingPhoneIdentifier, code: otpCode }),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok || !data?.token) {
        setError(data?.error ?? 'Invalid or expired code. Please request a new one.')
        return
      }

      const result = await signIn('magic-link', {
        magicLinkToken: data.token,
        callbackUrl,
        redirect: false,
      })

      if (!result?.ok) {
        setError('Unable to sign in. Please request a new code.')
        return
      }

      router.replace(callbackUrl)
    } catch {
      setError('Something went wrong. Please try again.')
    } finally {
      setIsVerifyingCode(false)
    }
  }

  useEffect(() => {
    if (!phoneCodeStep || typeof window === 'undefined') return
    if (!('OTPCredential' in window) || !navigator.credentials) return

    const abortController = new AbortController()
    navigator.credentials
      .get({
        otp: { transport: ['sms'] },
        signal: abortController.signal,
      } as CredentialRequestOptions)
      .then((credential: unknown) => {
        const otpCredential = credential as { code?: string } | null
        if (!otpCredential?.code) return
        setOtpCode(otpCredential.code.replace(/\D/g, '').slice(0, 6))
      })
      .catch(() => {
        // Browsers that do not support WebOTP or users who dismiss prompts fall back to manual entry.
      })

    return () => {
      abortController.abort()
    }
  }, [phoneCodeStep])

  // Count down resend cooldown every second
  useEffect(() => {
    if (resendCooldownSeconds <= 0) return
    const id = setInterval(() => {
      setResendCooldownSeconds(prev => Math.max(0, prev - 1))
    }, 1000)
    return () => clearInterval(id)
  }, [resendCooldownSeconds])

  // Registration (or a refresh) can land with ?phone=...&codeSent=true after a code was already requested.
  useEffect(() => {
    if (!codeSent || !phoneFromUrl?.trim() || initializedCodeStepFromParams.current) return
    initializedCodeStepFromParams.current = true
    const identifier = phoneFromUrl.trim()
    setPendingPhoneIdentifier(identifier)
    setPhoneCodeStep(true)
    setResendCooldownSeconds(60)
    setPhoneDeliveryMessage('Enter the code we sent to your phone.')
    setError(null)
    setAccountNotice(null)
  }, [codeSent, phoneFromUrl])

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      validateOnMount
      onSubmit={async values => {
        if (isSubmittingRef.current) return
        isSubmittingRef.current = true
        const now = Date.now()
        if (now - lastSubmitTimeRef.current < 2000) {
          isSubmittingRef.current = false
          return // duplicate submit (e.g. Safari mobile delayed second event)
        }
        lastSubmitTimeRef.current = now
        setError(null)
        setSuccess(false)
        setAccountNotice(null)
        setPhoneDeliveryMessage(null)
        setEmailDeliveryMessage(null)
        const identifier = values.method === 'email' ? values.email.trim() : values.phone.trim()
        const idempotencyKey = crypto.randomUUID()
        try {
          if (values.method === 'phone') {
            const result = await requestMagicLinkCode(identifier, { idempotencyKey, callbackUrl })
            if (result.accountFound === false) {
              setAccountNotice(result.message ?? 'We could not find an account for this phone number.')
              return
            }
            setPendingPhoneIdentifier(identifier)
            setOtpCode('')
            setPhoneCodeStep(true)
            setResendCooldownSeconds(60)
            setSuccess(false)
            setPhoneDeliveryMessage(result.message ?? null)
            setPhoneFromUrl(identifier)
            setCodeSent(true)
            return
          }

          const result = await requestMagicLinkCode(identifier, { idempotencyKey, callbackUrl })
          if (result.accountFound === false) {
            setAccountNotice(result.message ?? 'We could not find an account for this email.')
            return
          }
          setEmailDeliveryMessage(
            result.message ?? 'If an account exists for this email, you will receive a sign-in link shortly.',
          )
          setSuccess(true)
        } catch {
          setError('Something went wrong. Please try again.')
        } finally {
          isSubmittingRef.current = false
        }
      }}
      validationSchema={FormSchema}
    >
      {({ isValid, errors, touched, isSubmitting, values, setFieldValue, handleBlur }) => (
        <>
          <Form className="mt-4">
            {!phoneCodeStep && (
              <fieldset>
                <legend className="mb-1 block text-sm font-medium text-black-100">Choose your sign in method</legend>
                <div className="mt-1 grid grid-cols-2 gap-2 rounded-lg bg-gray-100 p-1">
                  <button
                    id="magic-link-method-phone"
                    type="button"
                    aria-pressed={values.method === 'phone'}
                    onClick={() => {
                      setFieldValue('method', 'phone')
                      setSuccess(false)
                      setAccountNotice(null)
                      setEmailDeliveryMessage(null)
                    }}
                    className={`rounded-md px-3 py-2 text-sm font-medium transition ${
                      values.method === 'phone' ? 'bg-white text-gray-800 shadow' : 'text-gray-600 hover:text-gray-800'
                    }`}
                  >
                    Phone
                  </button>
                  <button
                    id="magic-link-method-email"
                    type="button"
                    aria-pressed={values.method === 'email'}
                    onClick={() => {
                      setFieldValue('method', 'email')
                      setPhoneCodeStep(false)
                      setPendingPhoneIdentifier('')
                      setOtpCode('')
                      setAccountNotice(null)
                      setPhoneDeliveryMessage(null)
                      setSuccess(false)
                      setEmailDeliveryMessage(null)
                    }}
                    className={`rounded-md px-3 py-2 text-sm font-medium transition ${
                      values.method === 'email' ? 'bg-white text-gray-800 shadow' : 'text-gray-600 hover:text-gray-800'
                    }`}
                  >
                    Email
                  </button>
                </div>
              </fieldset>
            )}

            {values.method === 'email' ? (
              <>
                <div className="mt-3">
                  <Label htmlFor="magic-link-email">Email</Label>
                </div>
                <input
                  id="magic-link-email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  placeholder="you@example.com"
                  className={`focus:border-indigo-500 focus:ring-indigo-500 block w-full rounded-md border-gray-300 sm:text-sm ${
                    touched.email && errors.email ? 'border-red-70' : ''
                  }`}
                  value={values.email}
                  onChange={event => setFieldValue('email', event.target.value)}
                  onBlur={handleBlur}
                />
                <FieldError>{touched.email ? errors.email : null}</FieldError>
              </>
            ) : !phoneCodeStep ? (
              <>
                <div className="mt-3">
                  <Label htmlFor="magic-link-phone">Phone number</Label>
                </div>
                <input
                  id="magic-link-phone"
                  name="phone"
                  type="tel"
                  autoComplete="tel"
                  inputMode="tel"
                  placeholder="555-555-5555"
                  className={`focus:border-indigo-500 focus:ring-indigo-500 block w-full rounded-md border-gray-300 sm:text-sm ${
                    touched.phone && errors.phone ? 'border-red-70' : ''
                  }`}
                  value={values.phone}
                  onChange={event => setFieldValue('phone', formatPartialPhoneNumber(event.target.value))}
                  onBlur={handleBlur}
                />
                <FieldError>{touched.phone ? errors.phone : null}</FieldError>
              </>
            ) : null}

            {accountNotice && !phoneCodeStep && (
              <div className="mt-3 rounded-md border-2 border-black-100 bg-yellow-light px-3 py-2 text-center text-sm text-gray-800">
                {accountNotice}
              </div>
            )}
            {!success && !phoneCodeStep && (
              <button
                type="submit"
                disabled={!isValid || isSubmitting}
                style={{ touchAction: 'manipulation' }}
                className="hover:bg-bright-blue-100 mt-3 w-full rounded-lg bg-bright-blue-50 px-4 py-2 text-[16px] font-semibold text-white transition disabled:cursor-not-allowed disabled:bg-gray-300"
              >
                {isSubmitting
                  ? values.method === 'phone'
                    ? 'Sending code...'
                    : 'Sending link...'
                  : values.method === 'phone'
                    ? 'Send code'
                    : 'Send sign-in link'}
              </button>
            )}
            {success && !phoneCodeStep && emailDeliveryMessage && (
              <div className="mt-2 text-center">
                <p className="text-green-700 text-sm">{emailDeliveryMessage}</p>
                <button
                  type="button"
                  onClick={() => {
                    setSuccess(false)
                    setEmailDeliveryMessage(null)
                  }}
                  className="mt-1.5 text-center text-sm text-gray-600 underline underline-offset-2 hover:text-gray-800"
                >
                  Didn&apos;t receive it? Send another link
                </button>
              </div>
            )}
            {!phoneCodeStep && <FieldError>{error}</FieldError>}
          </Form>

          {phoneCodeStep && values.method === 'phone' && (
            <div className="mt-6">
              <p className="mb-5 text-center text-sm text-gray-600">
                Code sent to{' '}
                <span className="font-medium text-gray-800">
                  {pendingPhoneIdentifier || 'your phone'}
                </span>
              </p>
              <OtpSlotsInput
                value={otpCode}
                onChange={setOtpCode}
                disabled={isVerifyingCode}
                autoFocus
                aria-label={`6-digit sign-in code sent to ${pendingPhoneIdentifier || 'your phone'}`}
              />
              <button
                type="button"
                disabled={otpCode.length !== 6 || isVerifyingCode}
                style={{ touchAction: 'manipulation' }}
                onClick={verifyPhoneCode}
                className="hover:bg-bright-blue-100 mt-6 w-full rounded-lg bg-bright-blue-50 px-4 py-2 text-[16px] font-semibold text-white transition disabled:cursor-not-allowed disabled:bg-gray-300"
              >
                {isVerifyingCode ? 'Verifying code...' : 'Verify code and sign in'}
              </button>
              <button
                type="button"
                disabled={resendCooldownSeconds > 0 || isResendingCode}
                style={{ touchAction: 'manipulation' }}
                onClick={async () => {
                  const now = Date.now()
                  if (now - lastSubmitTimeRef.current < 2000) return
                  if (!pendingPhoneIdentifier || isResendingCode || resendCooldownSeconds > 0) return
                  if (isSubmittingRef.current) return
                  lastSubmitTimeRef.current = now
                  isSubmittingRef.current = true
                  setError(null)
                  setIsResendingCode(true)
                  try {
                    const result = await requestMagicLinkCode(pendingPhoneIdentifier, {
                      idempotencyKey: crypto.randomUUID(),
                      callbackUrl,
                    })
                    setOtpCode('')
                    setResendCooldownSeconds(60)
                    if (result.message) {
                      setPhoneDeliveryMessage(result.message)
                    }
                  } catch {
                    setError('Something went wrong. Please try again.')
                  } finally {
                    setIsResendingCode(false)
                    isSubmittingRef.current = false
                  }
                }}
                className="mt-6 w-full text-center text-sm text-gray-600 underline underline-offset-2 hover:text-gray-800 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isResendingCode
                  ? 'Sending another code...'
                  : resendCooldownSeconds > 0
                    ? `Send another code (available in ${resendCooldownSeconds}s)`
                    : "Didn't receive it? Send another code"}
              </button>
              <p className="mt-3 text-center text-sm text-gray-600">or</p>
              <button
                type="button"
                onClick={() => {
                  initializedCodeStepFromParams.current = false
                  setPhoneCodeStep(false)
                  setPendingPhoneIdentifier('')
                  setOtpCode('')
                  setResendCooldownSeconds(0)
                  setError(null)
                  setPhoneDeliveryMessage(null)
                  setAccountNotice(null)
                  void setPhoneFromUrl(null)
                  void setCodeSent(null)
                }}
                className="mt-1.5 w-full text-center text-sm text-gray-600 underline underline-offset-2 hover:text-gray-800"
              >
                Use a different phone number
              </button>
              <FieldError>{error}</FieldError>
            </div>
          )}
        </>
      )}
    </Formik>
  )
}
